import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiTrendingUp, FiCheck } = FiIcons;

const InvestmentSection = () => {
  return (
    <section className="investment-section py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Investment & <span className="text-blue-600">Running Cost</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          <motion.div 
            className="investment-details"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Running Cost Breakdown</h3>
              <p className="text-lg text-gray-700 mb-6">
                Running cost for your software company starts from just <span className="font-bold text-blue-600">₹7,500/month</span>.
              </p>
              
              <div className="comparison-table bg-gray-50 p-6 rounded-xl">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="font-semibold text-gray-900 border-b pb-2">Item</div>
                  <div className="font-semibold text-gray-900 border-b pb-2">Amount (Approx.)</div>
                  
                  <div className="text-gray-700 py-2">Monthly software cost</div>
                  <div className="text-blue-600 font-bold py-2">₹7,500</div>
                  
                  <div className="text-gray-700 py-2">1 new client at ₹5,000</div>
                  <div className="text-green-600 font-bold py-2">Covers most of cost</div>
                  
                  <div className="text-gray-700 py-2">2+ clients</div>
                  <div className="text-green-600 font-bold py-2">You're in profit</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="roi-calculation"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-2xl border-2 border-green-200">
              <SafeIcon icon={FiTrendingUp} className="text-4xl text-green-600 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Expected Returns</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="text-green-600" />
                  <span className="text-lg text-gray-700">
                    <span className="font-bold text-green-600">3× or more</span> every month once set up correctly
                  </span>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Monthly Investment</div>
                    <div className="text-2xl font-bold text-blue-600">₹7,500</div>
                  </div>
                </div>
                
                <div className="text-center text-2xl font-bold text-gray-700">VS</div>
                
                <div className="bg-white p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Monthly Returns (Conservative)</div>
                    <div className="text-2xl font-bold text-green-600">₹25,000+</div>
                  </div>
                </div>
                
                <div className="bg-yellow-100 p-4 rounded-lg border border-yellow-300">
                  <div className="text-center">
                    <div className="text-sm text-gray-700">ROI</div>
                    <div className="text-3xl font-bold text-yellow-700">333%+</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="reminder-box bg-yellow-50 border-2 border-yellow-300 p-8 rounded-2xl mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <SafeIcon icon={FiDollarSign} className="text-4xl text-yellow-600 mx-auto mb-4" />
            <p className="text-xl text-gray-800 font-semibold">
              Apply only if you're serious and can afford to run it.
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.a
            href="https://abdul.cc/earnwithabdul"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold text-xl px-12 py-6 rounded-full hover:from-blue-700 hover:to-blue-900 transition-all duration-300 transform hover:scale-105 shadow-2xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Yes, I Can Invest & I'm Ready to Build
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentSection;